"use client";

import { Plus } from "lucide-react";
import { useState, useRef, useEffect } from "react";

/**
 * Reusable App FAQ Section
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {Array} props.faqs - Array of FAQ items { question, answer }
 */

const FaqItem = ({ faq, isOpen, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          setHeight(contentRef.current.scrollHeight + 16);
        }
      });
    } else {
      setHeight(0);
    }
  }, [isOpen, faq.answer]);

  return (
    <div className="rounded-2xl overflow-hidden">
      <div className="bg-white/5 px-6 py-4 cursor-pointer" onClick={onToggle}>
        {/* Question Row */}
        <div className="flex items-center justify-between w-full">
          <h3
            className="text-xl font-medium text-white tracking-[-0.2px] pr-4 transition-[line-height] duration-300 ease-in-out"
            style={{ lineHeight: isOpen ? "28px" : "32px" }}
          >
            {faq.question}
          </h3>
          <div className="flex items-center justify-center w-8 h-8 shrink-0">
            <Plus
              className={`w-5 h-5 text-white transition-transform duration-300 ease-in-out ${isOpen ? "rotate-45" : "rotate-0"}`}
            />
          </div>
        </div>

        {/* Answer - Animated */}
        <div
          className="overflow-hidden transition-[height] duration-300 ease-in-out"
          style={{ height }}
        >
          <div ref={contentRef}>
            <div className="mt-6 pb-6">
              <p
                className="text-[#c4c4c4] text-base tracking-[-0.032px]"
                style={{ lineHeight: "24px" }}
              >
                {faq.answer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppFAQSection = ({ title = "FAQs", faqs = [] }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 relative z-10 overflow-hidden bg-[#2e2e2e]">
      <div className="mx-auto px-6 md:px-16 lg:px-32 relative z-10 max-w-[1440px]">
        {/* Section Title */}
        <div className="flex flex-col items-center justify-center mb-12">
          <h2
            className="text-3xl md:text-[40px] font-normal text-white tracking-[-0.24px]"
            style={{ lineHeight: 1.2 }}
          >
            {title}
          </h2>
        </div>

        {/* FAQ Items */}
        <div className="flex flex-col gap-5">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => toggleFaq(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
